// The two the wire contract accepts. VideoFacingModeEnum also carries 'left'
// and 'right', which no phone this runs on reports and the backend rejects.
export type CameraFacing = 'user' | 'environment';

export interface CameraFacingInterval {
  facing: CameraFacing;
  started_at: string;
  ended_at: string;
}

interface OpenInterval {
  facing: CameraFacing;
  startedAt: number;
}

const MAX_BUFFERED_INTERVALS = 100;

function toInterval({ facing, startedAt }: OpenInterval, endedAt: number): CameraFacingInterval {
  return {
    facing,
    started_at: new Date(startedAt).toISOString(),
    ended_at: new Date(endedAt).toISOString(),
  };
}

export function createFacingIntervalBuffer() {
  let closed: CameraFacingInterval[] = [];
  let open: OpenInterval | null = null;

  return {
    // `facing` is null while nothing is being filmed, which closes the current
    // stretch: these intervals annotate video, so a stretch with no frames must
    // not be labelled with a camera.
    accept(facing: CameraFacing | null, at: number) {
      if (open?.facing === facing) {
        return;
      }
      // A stretch that opens and closes inside the same millisecond describes no
      // frames, and it would reach the backend sharing its successor's start —
      // which is the key the two are upserted on, so the pair is rejected whole.
      if (open && at > open.startedAt) {
        closed = [...closed, toInterval(open, at)].slice(-MAX_BUFFERED_INTERVALS);
      }
      open = facing ? { facing, startedAt: at } : null;
    },
    drain(): CameraFacingInterval[] {
      const drained = closed;
      closed = [];
      return drained;
    },
    // The open stretch is sent bounded at "still filming as of now" and re-sent
    // as it grows, so a call that dies without warning leaves it short rather
    // than unbounded. It is never restored on failure — the next flush rebuilds
    // it from the same open interval.
    openSnapshot(at: number): CameraFacingInterval | null {
      return open && toInterval(open, at);
    },
    restore(unsent: CameraFacingInterval[]) {
      closed = [...unsent, ...closed].slice(-MAX_BUFFERED_INTERVALS);
    },
    get size() {
      return closed.length + (open ? 1 : 0);
    },
  };
}
